import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Search, Plus, X, Percent, Download, Info } from "lucide-react";

const LS_KEY = "clazing_verkoopmarges";
const DEFAULT_MARGES = [5, 10, 15, 20, 25, 30];
const r1 = (v) => Math.round((Number(v) || 0) * 10) / 10;
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

const loadMarges = () => {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY));
    if (Array.isArray(v) && v.length) return [...new Set(v.map(Number).filter((n) => n > 0 && n <= 60))].sort((a, b) => a - b);
  } catch (e) { /* default */ }
  return [...DEFAULT_MARGES];
};

// Verkoopprijzen per product: gemiddelde kostprijs over ALLE producties tot nu toe,
// met instelbare opslag-percentages. Verkoopprijs = kostprijs x (1 + marge%).
// Puur een read-only overzicht uit bestaande productiedata (rows) — geen opslag of
// beveiliging aangeraakt. Actualiseert vanzelf zodra er productie bijkomt.
export default function VerkoopprijzenTab({ xl }) {
  const { products, rows, euro, kgf, toNum, sum, safeDiv, Card, Kpi, btn, artByNaam } = xl;
  const [q, setQ] = useState("");
  const [marges, setMarges] = useState(loadMarges);
  const [nieuw, setNieuw] = useState("");

  useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(marges)); } catch (e) { /* noop */ } }, [marges]);

  const setMarge = (i, val) => {
    const n = Math.min(60, Math.max(0, Math.round(Number(String(val).replace(",", ".")) * 10) / 10));
    setMarges((arr) => arr.map((m, idx) => (idx === i ? n : m)));
  };
  const commitMarges = () => setMarges((arr) => [...new Set(arr.filter((n) => n > 0 && n <= 60))].sort((a, b) => a - b));
  const addMarge = () => {
    const n = Math.round(Number(String(nieuw).replace(",", ".")) * 10) / 10;
    if (!(n > 0) || n > 60) return;
    setMarges((arr) => (arr.includes(n) ? arr : [...arr, n].sort((a, b) => a - b)));
    setNieuw("");
  };
  const removeMarge = (i) => setMarges((arr) => arr.filter((_, idx) => idx !== i));
  const resetMarges = () => setMarges([...DEFAULT_MARGES]);

  const stats = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (products || [])
      .map((p) => {
        const f = (rows || []).filter((r) => r.product === p.naam);
        const g = sum(f, (r) => r.geproduceerdKg);
        const k = sum(f, (r) => r.totaleKosten);
        return { id: p.id, naam: p.naam, art: p.artikelnummer || artByNaam?.[p.naam] || "", g, kp: safeDiv(k, g), n: f.length };
      })
      .filter((s) => s.g > 0)
      .filter((s) => !ql || String(s.naam).toLowerCase().includes(ql) || String(s.art).toLowerCase().includes(ql))
      .sort((a, b) => {
        const na = parseFloat(String(a.art).replace(/[^0-9.]/g, "")), nb = parseFloat(String(b.art).replace(/[^0-9.]/g, ""));
        const va = isNaN(na) ? Infinity : na, vb = isNaN(nb) ? Infinity : nb;
        return va - vb || String(a.art).localeCompare(String(b.art), undefined, { numeric: true }) || String(a.naam).localeCompare(String(b.naam));
      });
  }, [products, rows, q, sum, safeDiv, artByNaam]);

  const gemKp = stats.length ? stats.reduce((s, x) => s + x.kp, 0) / stats.length : 0;

  const exportXlsx = () => {
    const header = ["Artikelnr", "Product", "Geproduceerd (kg)", "Gem. kostprijs/kg", ...marges.map((m) => `+${m}% verkoop/kg`)];
    const aoa = [
      ["Verkoopprijzen per product — Clazing Convenience Food B.V."],
      [`Export ${new Date().toLocaleDateString("nl-NL")} · gemiddelde kostprijs over alle producties tot nu toe · verkoopprijs = kostprijs × (1 + marge%)`],
      [],
      header,
    ];
    stats.forEach((s) => aoa.push([s.art, s.naam, r1(s.g), r2(s.kp), ...marges.map((m) => r2(s.kp * (1 + m / 100)))]));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 12 }, { wch: 40 }, { wch: 16 }, { wch: 16 }, ...marges.map(() => ({ wch: 16 }))];
    const lastCol = Math.max(3, header.length - 1);
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } }, { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Verkoopprijzen");
    XLSX.writeFile(wb, `verkoopprijzen_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="mb-1 text-3xl font-bold tracking-tight text-slate-900">Verkoopprijzen</h1>
          <p className="max-w-3xl text-sm text-slate-500">Per product de <b>gemiddelde kostprijs tot nu toe</b> (over alle producties) met per marge-% de bijbehorende <b>verkoopprijs per kg</b>. Verkoopprijs = kostprijs × (1 + marge%). Verkopers bepalen hiermee per klant welk percentage ze geven. Actualiseert vanzelf na elke productie.</p>
        </div>
        <button className={btn} data-testid="verkoop-export" onClick={exportXlsx} disabled={!stats.length}><Download className="mr-2 inline h-4 w-4" />Exporteer Excel</button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-3">
        <Kpi label="Producten (geproduceerd)" value={String(stats.length)} />
        <Kpi label="Gem. kostprijs/kg" value={stats.length ? euro(gemKp) : "—"} sub="Gemiddeld over de getoonde producten" />
        <Kpi label="Marge-staffels" value={String(marges.length)} sub="Zelf aanpasbaar tot 60%" />
      </div>

      <Card className="mb-6">
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500"><Percent className="h-4 w-4 text-[#1C64B0]" /> Marge-percentages (opslag op kostprijs)</div>
        <div className="flex flex-wrap items-center gap-2" data-testid="verkoop-marge-editor">
          {marges.map((m, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full border border-[#1C64B0]/30 bg-[#1C64B0]/5 pl-2.5 pr-1 py-1 text-sm font-semibold text-[#1C64B0]">
              <input
                type="number" step="1" min="1" max="60"
                data-testid={`verkoop-marge-inp-${i}`}
                value={m}
                onChange={(e) => setMarge(i, e.target.value)}
                onBlur={commitMarges}
                className="w-12 bg-transparent text-right outline-none"
              />%
              <button type="button" data-testid={`verkoop-marge-del-${i}`} onClick={() => removeMarge(i)} title="Verwijder deze staffel" className="ml-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-[#1C64B0]/60 hover:bg-[#1C64B0]/10 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
            </span>
          ))}
          <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 pl-2.5 pr-1 py-1">
            <input
              type="number" step="1" min="1" max="60"
              data-testid="verkoop-marge-nieuw"
              value={nieuw}
              onChange={(e) => setNieuw(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addMarge(); }}
              placeholder="bijv. 14"
              className="w-16 bg-transparent text-sm outline-none"
            />
            <button type="button" data-testid="verkoop-marge-add" onClick={addMarge} title="Staffel toevoegen (max 60%)" className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1C64B0] text-white hover:bg-[#154E8A]"><Plus className="h-4 w-4" /></button>
          </span>
          <button type="button" data-testid="verkoop-marge-reset" onClick={resetMarges} className="ml-1 text-xs font-semibold text-slate-500 hover:text-[#1C64B0] hover:underline">Standaard (5–30%)</button>
        </div>
        <p className="mt-2 flex items-center gap-1 text-[11px] text-slate-400"><Info className="h-3 w-3" /> Pas een getal aan, voeg staffels toe met + (tot 60%) of verwijder met ×. Deze instelling wordt op dit apparaat onthouden.</p>
      </Card>

      <div className="mb-4">
        <div className="relative inline-block">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <input data-testid="verkoop-zoek" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op artikelnummer of naam…" className="w-80 rounded-md border border-slate-300 bg-white py-2 pl-8 pr-3 text-sm" />
        </div>
      </div>

      {stats.length === 0 ? (
        <Card><p className="text-sm text-slate-500" data-testid="verkoop-empty">Nog geen geproduceerde producten. Zodra er productie is ingevoerd, verschijnen de verkoopprijzen hier automatisch.</p></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="verkoop-table">
              <thead>
                <tr className="border-b-2 border-slate-200 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                  <th className="sticky left-0 bg-white px-3 py-2">Artikelnr</th>
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2 text-right">Geproduceerd</th>
                  <th className="px-3 py-2 text-right">Gem. kostprijs/kg</th>
                  {marges.map((m, i) => <th key={i} className="px-3 py-2 text-right text-[#1C64B0]">+{m}%</th>)}
                </tr>
              </thead>
              <tbody>
                {stats.map((s) => (
                  <tr key={s.id} data-testid="verkoop-row" className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="sticky left-0 bg-white px-3 py-2 text-slate-500">{s.art || "—"}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{s.naam}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{kgf(s.g)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900">{euro(s.kp)}</td>
                    {marges.map((m, i) => <td key={i} className="px-3 py-2 text-right font-semibold text-[#1C64B0]" data-testid="verkoop-cel">{euro(s.kp * (1 + m / 100))}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-slate-400">Gemiddelde kostprijs/kg = totale productiekosten ÷ totaal geproduceerde kg over alle producties tot nu toe. Verkoopprijs/kg per staffel = kostprijs × (1 + marge%).</p>
        </Card>
      )}
    </>
  );
}
